import { gql } from '@apollo/client';

// ============================================================================
// ADMIN USER QUERIES
// ============================================================================

export const GET_CURRENT_ADMIN = gql`
  query GetCurrentAdmin {
    currentAdminUser {
      id
      email
      fullName
      role
      isActive
      permissions
      lastLoginAt
      createdAt
    }
  }
`;

export const GET_ADMIN_USERS = gql`
  query GetAdminUsers {
    adminUsers {
      id
      email
      fullName
      role
      isActive
      lastLoginAt
      createdAt
      notes
    }
  }
`;

// ============================================================================
// DASHBOARD & ANALYTICS
// ============================================================================

export const GET_ADMIN_DASHBOARD = gql`
  query GetAdminDashboard {
    adminDashboard {
      totalUsers
      totalOrganizations
      activeSubscriptions
      mrrCents
      arrCents
      newUsersToday
      newOrganizationsToday
      activeUsersToday
      freePlanCount
      proPlanCount
      enterprisePlanCount
      userGrowthRate
      revenueGrowthRate
      churnRate
      avgContextsPerOrg
      avgProjectsPerOrg
      lastUpdated
    }
  }
`;

export const GET_DAILY_METRICS = gql`
  query GetDailyMetrics($daysBack: Int!) {
    adminDailyMetrics(daysBack: $daysBack) {
      date
      totalUsers
      newUsersToday
      activeUsersToday
      totalOrganizations
      newOrganizationsToday
      freePlanOrgs
      proPlanOrgs
      enterprisePlanOrgs
      promoPlanOrgs
      activeSubscriptions
      mrr
      newMrrToday
      churnedMrrToday
      contextsCreatedToday
      totalContexts
      projectsCreatedToday
      totalProjects
      avgContextsPerOrg
      avgProjectsPerOrg
      calculatedAt
    }
  }
`;

export const GET_MONTHLY_METRICS = gql`
  query GetMonthlyMetrics($monthsBack: Int!) {
    adminMonthlyMetrics(monthsBack: $monthsBack) {
      id
      year
      month
      mau
      newUsers
      churnedUsers
      mrr
      arr
      totalRevenue
      userChurnRate
      revenueChurnRate
      userGrowthRate
      revenueGrowthRate
      calculatedAt
    }
  }
`;

// ============================================================================
// USER MANAGEMENT
// ============================================================================

export const LIST_USERS = gql`
  query ListUsers($filters: UserListFiltersInput) {
    adminListUsers(filters: $filters) {
      users {
        userId
        email
        fullName
        createdAt
        lastLoginAt
        isActive
        primaryOrganizationId
        totalOrganizations
        totalContextsCreated
        totalProjectsCreated
        currentPlan
        isPaying
        lastUpdated
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

// ============================================================================
// ORGANIZATION MANAGEMENT
// ============================================================================

export const LIST_ORGANIZATIONS = gql`
  query ListOrganizations($filters: OrganizationListFiltersInput) {
    adminListOrganizations(filters: $filters) {
      organizations {
        organizationId
        name
        slug
        plan
        isPromoAccount
        promoCodeId
        stripeCustomerId
        stripeSubscriptionId
        memberCount
        projectCount
        contextCount
        maxMembers
        maxProjects
        maxContexts
        isActive
        createdAt
        mrrCents
        lastUpdated
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

// ============================================================================
// AUDIT LOGS
// ============================================================================

export const GET_AUDIT_LOGS = gql`
  query GetAuditLogs($filters: AuditLogFiltersInput) {
    adminAuditLogs(filters: $filters) {
      logs {
        id
        adminUserId
        adminEmail
        action
        resourceType
        resourceId
        changes
        ipAddress
        userAgent
        success
        errorMessage
        createdAt
        metadata
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

// ============================================================================
// PROMO CODES
// ============================================================================

export const LIST_PROMO_CODES = gql`
  query ListPromoCodes($includeInactive: Boolean) {
    adminListPromoCodes(includeInactive: $includeInactive) {
      id
      code
      plan
      discountType
      discountAmount
      maxUses
      currentUses
      expiresAt
      allowedEmails
      allowedEmailDomains
      createdBy
      isActive
      createdAt
    }
  }
`;

// ============================================================================
// ADMIN MUTATIONS
// ============================================================================

// Admin User Management
export const LIST_ADMIN_USERS = gql`
  query ListAdminUsers {
    adminListAdmins {
      id
      clerkUserId
      email
      fullName
      role
      isActive
      isMfaEnabled
      allowedIpAddresses
      lastLoginAt
      lastLoginIp
      createdBy
      createdAt
      updatedAt
      notes
      permissions
    }
  }
`;

export const CREATE_ADMIN_USER = gql`
  mutation CreateAdminUser($input: CreateAdminInput!) {
    adminCreateAdmin(input: $input) {
      id
      email
      fullName
      role
      permissions
      createdAt
    }
  }
`;

export const UPDATE_ADMIN_USER = gql`
  mutation UpdateAdminUser($adminUserId: Int!, $updates: UpdateAdminInput!) {
    adminUpdateAdmin(adminUserId: $adminUserId, updates: $updates) {
      id
      email
      fullName
      role
      isActive
      updatedAt
    }
  }
`;

export const DEACTIVATE_ADMIN_USER = gql`
  mutation DeactivateAdminUser($adminUserId: Int!) {
    adminDeactivateAdmin(adminUserId: $adminUserId)
  }
`;

// User Management
export const DEACTIVATE_USER = gql`
  mutation DeactivateUser($userId: String!) {
    adminDeactivateUser(userId: $userId)
  }
`;

// Organization Management
export const CHANGE_ORG_PLAN = gql`
  mutation ChangeOrgPlan($organizationId: String!, $newPlan: String!) {
    adminChangeOrgPlan(organizationId: $organizationId, newPlan: $newPlan)
  }
`;

// Promo Code Management
export const CREATE_PROMO_CODE = gql`
  mutation CreatePromoCode($input: CreatePromoCodeInput!) {
    adminCreatePromoCode(input: $input) {
      id
      code
      plan
      discountType
      isActive
      createdAt
    }
  }
`;

export const DEACTIVATE_PROMO_CODE = gql`
  mutation DeactivatePromoCode($promoCodeId: Int!) {
    adminDeactivatePromoCode(promoCodeId: $promoCodeId)
  }
`;
